import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../state/store";
import {
  resetApps,
  resetErrorCharts,
  resetEvents,
  resetMarketEvents,
  setAppsLoading,
  setDatesRange,
  setEventsFetched,
  setEventsRefresh,
  setIsFiltersSet,
  setLoadingCharts,
  setLocationsError,
  setLocationsLoading,
  setMarketEventsFetched,
  setMarketEventsRefresh,
  setPeriod,
  setSelectedFromDate,
  setSelectedPeriod,
  setSelectedToDate
} from "../../state/ducks";
import { selectUserMarkets, selectLocations } from "../../state/selectors";

import { alignItems, appUserRoles, dateRangeFilterOptions, strCode } from "../../consts";
import {
  appsService,
  appUserService,
  locationsService,
  log,
  notificationService,
  rumEventsService,
  t
} from "../../services";
import { dateUtil, objectUtil, rumEventsUtil } from "../../utils";
import { useSelectedDashboardFilters } from "../../hooks";
import { withSection } from "../../hoc";

import { Dashboard, DashboardFiltersPanel } from "../../containers";
import {
  FlexContainers,
  Heading5,
  PageContainer,
  Paragraph2,
  RefreshButton,
  WrapperContainer
} from "../../components";

import {
  marketEvents,
  chartIds as defaultChartIds,
  marketAdminChartIds,
  marketChartIds as defaultMarketChartIds,
  marketAdminRumEvents,
  marketAdminMarketRumEvents
} from "./config";

const getArrayFilterValue = (selectedFilter, filters, field = "value") => {
  return selectedFilter?.length && !selectedFilter.find((val) => val.id === 0)
    ? selectedFilter.map((opt) => opt[field]).join(",")
    : filters?.length && !filters.find((val) => val.id === 0)
    ? filters.map((opt) => opt[field]).join(",")
    : null;
};

const DashboardPage = () => {
  const device = useSelector((store) => store.dashboardFilters.device);
  const location = useSelector((store) => store.dashboardFilters.location);
  const platform = useSelector((store) => store.dashboardFilters.platform);
  const browser = useSelector((store) => store.dashboardFilters.browser);
  const market = useSelector((store) => store.dashboardFilters.market);
  const appType = useSelector((store) => store.dashboardFilters.appType);
  const connectivity = useSelector((store) => store.dashboardFilters.connectivity);
  const appsLoading = useSelector((store) => store.apps?.loading);
  const appsError = useSelector((store) => store.apps?.error);

  const userAppType = useSelector((store) => store.configs?.appType);

  const eventsFetched = useSelector((store) => store.rum.eventsFetched);
  const marketEventsFetched = useSelector((store) => store.rum.marketEventsFetched);

  const loadingCharts = useSelector((store) => store.rum.loadingCharts);
  const refresh = useSelector((store) => store.rum.refresh);

  const marketRefresh = useSelector((store) => store.rum.marketRefresh);

  const isFiltersSet = useSelector((store) => store.dashboardFilters.isFiltersSet);

  const {
    selectedDevice,
    selectedLocation,
    selectedMarkets,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity,
    selectedPeriod,
    fromDate,
    toDate
  } = useSelectedDashboardFilters();

  const isMarketAdminOnly =
    appUserService.hasRole(appUserRoles.MarketAdmin) &&
    !appUserService.hasRole(appUserRoles.ApplicationManager);

  const chartIds = isMarketAdminOnly ? marketAdminChartIds : defaultChartIds;
  const marketChartIds = isMarketAdminOnly ? marketAdminChartIds : defaultMarketChartIds;

  const isLoading = useMemo(() => {
    return loadingCharts && loadingCharts?.length;
  }, [loadingCharts]);

  const onSavingFilters = useCallback(() => {
    dispatch(setIsFiltersSet(true));

    fetchLogsData(false);
    fetchMarketLogsData(false);
  }, [
    selectedLocation,
    selectedMarkets,
    selectedDevice,
    selectedPeriod,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity,
    fromDate,
    toDate
  ]);

  const onUpdateDateRange = (from, to, dateRangePeriod) => {
    dispatch(setSelectedFromDate(from));
    dispatch(setSelectedToDate(to));
    dispatch(setSelectedPeriod(dateRangePeriod));
  };

  const fetchEventsWrapper = useCallback(
    ({
      isRefresh,
      isEventsFetched = false,
      resetEventsCallback = () => {},
      fetchEventsCallback = () => {}
    }) => {
      if (!fromDate || !toDate || !selectedPeriod) {
        return;
      }

      if (!isRefresh && isEventsFetched) {
        return;
      }

      resetEventsCallback();

      if (fromDate && toDate && selectedPeriod) {
        if (!isRefresh) {
          dispatch(setPeriod(selectedPeriod));

          if (selectedPeriod === dateRangeFilterOptions.RANGE) {
            dispatch(setDatesRange(fromDate, toDate));
          }
        }

        let updatedToDate = toDate;

        // if the end date is today, we need to update its hours, minutes and second values before
        // sending the request to fetch logs data since the dates are provided as timestaps in the
        // request body so every second is important
        if (dateUtil.isToday(toDate)) {
          const today = new Date();
          updatedToDate.setHours(today.getHours(), today.getMinutes());
          updatedToDate.setSeconds(today.getSeconds());
        }

        const selectedDeviceName =
          device && device.id !== 0
            ? device.value
            : selectedDevice && selectedDevice.id !== 0
            ? selectedDevice.value
            : null;

        fetchEventsCallback(selectedDeviceName);
      }
    },
    [fromDate, toDate, selectedPeriod, device, selectedDevice]
  );

  const fetchLogsData = useCallback(
    (isRefresh) => {
      const fetchEventsCallback = async (selectedDeviceName) => {
        dispatch(resetErrorCharts());
        dispatch(setLoadingCharts(rumEventsUtil.getChartsBySelectedGroups(chartIds)));

        const eventNames = isMarketAdminOnly
          ? marketAdminRumEvents
          : rumEventsUtil.getEventsBySelectedGroups();

        await rumEventsService.fetchEventsList({
          eventNames,
          isMarketEvents: false,
          params: {
            startTime: fromDate.getTime(),
            endTime: toDate.getTime(),
            market:
              selectedAppType.length === 1 ? getArrayFilterValue(selectedMarkets, market) : null,
            appType: getArrayFilterValue(selectedAppType, appType, "id"),
            store:
              selectedMarkets?.length === 1
                ? getArrayFilterValue(selectedLocation, location, "id")
                : null,
            osName: getArrayFilterValue(selectedPlatform, platform),
            browserName: getArrayFilterValue(selectedBrowser, browser),
            connectionType: getArrayFilterValue(selectedConnectivity, connectivity, "id")
          },
          deviceName: selectedDeviceName
        });

        dispatch(setEventsFetched(true));
        dispatch(setEventsRefresh(false));
      };

      return fetchEventsWrapper({
        isRefresh,
        isEventsFetched: eventsFetched,
        resetEventsCallback: () => dispatch(resetEvents()),
        fetchEventsCallback
      });
    },
    [
      fromDate,
      toDate,
      location,
      platform,
      browser,
      userAppType,

      selectedMarkets,
      selectedLocation,
      selectedPlatform,
      selectedBrowser,
      selectedAppType,
      selectedGroup,
      selectedConnectivity,
      eventsFetched
    ]
  );

  const fetchMarketLogsData = useCallback(
    (isRefresh) => {
      const fetchEventsCallback = async (selectedDeviceName) => {
        dispatch(resetErrorCharts());
        dispatch(setLoadingCharts(rumEventsUtil.getChartsBySelectedGroups(marketChartIds)));

        const eventNames = isMarketAdminOnly
          ? marketAdminMarketRumEvents
          : rumEventsUtil.getEventsBySelectedGroups(marketEvents);

        await rumEventsService.fetchEventsList({
          eventNames,
          isMarketEvents: true,
          params: {
            startTime: fromDate.getTime(),
            endTime: toDate.getTime(),
            market:
              selectedAppType.length === 1 ? getArrayFilterValue(selectedMarkets, market) : null,
            appType: getArrayFilterValue(selectedAppType, appType, "id"),
            osName: getArrayFilterValue(selectedPlatform, platform),
            browserName: getArrayFilterValue(selectedBrowser, browser),
            connectionType: getArrayFilterValue(selectedConnectivity, connectivity, "id")
          },
          deviceName: selectedDeviceName
        });

        dispatch(setMarketEventsFetched(true));
        dispatch(setMarketEventsRefresh(false));
      };

      return fetchEventsWrapper({
        isRefresh,
        isEventsFetched: marketEventsFetched,
        resetEventsCallback: () => dispatch(resetMarketEvents()),
        fetchEventsCallback
      });
    },
    [
      fromDate,
      toDate,
      platform,
      browser,
      userAppType,

      selectedMarkets,
      selectedPlatform,
      selectedBrowser,
      selectedAppType,
      selectedGroup,
      selectedConnectivity,
      marketEventsFetched
    ]
  );

  const fetchLocations = async () => {
    const locations = selectLocations()(getState());
    const markets = selectUserMarkets()(getState());

    if (!(appUserService.hasRole(appUserRoles.MarketAdmin) && objectUtil.isEmpty(locations))) {
      return;
    }

    dispatch(setLocationsLoading(true));
    dispatch(setLocationsError(null));

    try {
      await Promise.all(
        markets?.map((market) => {
          return locationsService.fetchLocations(market);
        })
      );
    } catch (err) {
      dispatch(setLocationsError(err));

      log.error("DashboardPage: Failed to fetch locations", err);

      notificationService.showError({
        title: t(strCode.LOCATIONS_FETCH_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setLocationsLoading(false));
      dispatch(setLocationsError(false));
    }
  };

  const fetchApps = async () => {
    try {
      dispatch(setAppsLoading(true));
      dispatch(resetApps());
      await appsService.fetchApps();
    } catch (err) {
      log.error("DashboardPage: Failed to fetch apps", err);

      notificationService.showError({
        title: t(strCode.FETCH_APPS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setAppsLoading(false));
    }
  };

  useEffect(async () => {
    await fetchApps();
    await fetchLocations();
  }, []);

  useEffect(() => {
    if (refresh) {
      fetchLogsData(true);
    }
  }, [refresh]);

  useEffect(() => {
    if (marketRefresh) {
      fetchMarketLogsData(true);
    }
  }, [marketRefresh]);

  const handleRefresh = () => {
    dispatch(setEventsRefresh(true));
    dispatch(setMarketEventsRefresh(true));
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      {appsLoading ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
        </WrapperContainer>
      ) : appsError ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.USERS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          width="100%"
          height={isFiltersSet ? "auto" : "100%"}>
          <WrapperContainer Container={FlexContainers.FlexRowBetween} width={"100%"} gap={16}>
            <WrapperContainer Container={FlexContainers.FlexRowStart} alignItems={"center"} gap={8}>
              <Heading5>{t(strCode.DASHBOARD_TITLE)}</Heading5>
              {isFiltersSet && (
                <RefreshButton isLoading={!!isLoading} handleRefresh={handleRefresh} />
              )}
            </WrapperContainer>

            {isFiltersSet && (
              <DashboardFiltersPanel
                isLoading={!!isLoading}
                isMarketAdminOnly={isMarketAdminOnly}
                onUpdateDateRange={onUpdateDateRange}
                onSavingFilters={onSavingFilters}
                isFiltersSet
              />
            )}
          </WrapperContainer>

          {isFiltersSet ? (
            <Dashboard isMarketAdminOnly={isMarketAdminOnly} />
          ) : (
            <WrapperContainer Container={FlexContainers.FlexColumnStart} width="100%" height="100%">
              <DashboardFiltersPanel
                isLoading={!!isLoading}
                isMarketAdminOnly={isMarketAdminOnly}
                onUpdateDateRange={onUpdateDateRange}
                onSavingFilters={onSavingFilters}
              />
            </WrapperContainer>
          )}
        </WrapperContainer>
      )}
    </PageContainer>
  );
};

export default withSection(DashboardPage);
